/**
 * ============================================================================
 *  HelperLines — 节点对齐辅助虚线（蓝色细虚线）
 * ============================================================================
 *
 * 拖动节点对齐时，在画布上绘制蓝色细虚线指示对齐位置。
 *
 * 实现：一张覆盖画布的 <canvas>，读取 ReactFlow 视口 transform（x/y/zoom），
 * 把画布坐标的对齐线换算成屏幕坐标绘制。canvas 方式比逐条 SVG <line> 更省，
 * 且 pointer-events:none 不干扰任何交互。
 *
 * props:
 *   horizontal - 水平对齐线的 y（画布坐标），无则不画
 *   vertical   - 垂直对齐线的 x（画布坐标），无则不画
 */
import { useEffect, useRef } from 'react';
import { useStore } from 'reactflow';

const LINE_COLOR = '#3b82f6'; // 蓝色

const transformSelector = (s) => s.transform; // [x, y, zoom]

export default function HelperLines({ horizontal, vertical }) {
  const [tx, ty, zoom] = useStore(transformSelector);
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, width, height);

    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = 1;            // 细线，恒定 1px（屏幕坐标，不随 zoom 变粗）
    ctx.setLineDash([4, 3]);      // 虚线

    // 画布坐标 → 屏幕坐标：screen = canvas * zoom + transform
    if (typeof vertical === 'number') {
      const x = vertical * zoom + tx;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    if (typeof horizontal === 'number') {
      const y = horizontal * zoom + ty;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [horizontal, vertical, tx, ty, zoom, width, height]);

  return (
    <canvas
      ref={canvasRef}
      className="helper-lines"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9,
      }}
    />
  );
}
